/**
 * 캘린더 서비스 — Supabase CRUD (visibility 모델)
 *
 * 합의: .agent/plans/calendar_phase1_design.md + 2026-05-21 합의 update
 * - Firebase Auth JWT는 SupabaseClient가 자동 주입
 * - RLS 가 visibility 기반 자동 필터링 + 게스트 INSERT 차단
 */
import { supabase } from './SupabaseClient';
import type { TablesInsert } from '../types/supabase';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  CalendarEvent, EventAttendee, EventException, EventNotification,
  PlatformHoliday, WorkspaceHolidayConfig,
  EventVisibility, EventAuthor, AttendeeStatus,
  rowToEvent, rowToAttendee, rowToException, rowToNotification,
  rowToHoliday, rowToHolidayConfig,
} from '../models/CalendarEvent';

/**
 * Phase 2 (알림) — events 변경 후 server 측 reconcile callable 호출.
 * Cloud Tasks 등록·취소를 server 측에서 멱등하게 처리.
 * 호출 실패 시 reminder 등록 누락 가능하지만 events CRUD 자체는 막지 않음.
 */
async function callReconcileReminders(eventId: string): Promise<void> {
  try {
    const functions = getFunctions(getApp(), 'asia-northeast1');
    const reconcile = httpsCallable<{ eventId: string }, { scheduled: number; cancelled: number }>(
      functions, 'reconcileEventReminders',
    );
    await reconcile({ eventId });
  } catch (err) {
    console.warn('[CalendarService] reconcileEventReminders 호출 실패 (reminder 등록 누락 가능):', err);
  }
}

function getClient() {
  if (!supabase) throw new Error('[CalendarService] Supabase 클라이언트가 초기화되지 않았습니다.');
  return supabase;
}

// ═══════════════════════════════
//  Events — CRUD
// ═══════════════════════════════

/**
 * 워크스페이스의 일정 조회 (날짜 범위)
 * RLS 가 visibility/owner_channel_id/shared_channel_ids 기반 자동 필터.
 *
 * @param channelId  채널 캘린더용 (옵션) — owner_channel_id=X 또는 shared_channel_ids ∋ X 만
 */
export async function fetchEvents(params: {
  workspaceId: string;
  rangeStart: string;
  rangeEnd: string;
  channelId?: string;
}): Promise<CalendarEvent[]> {
  const sb = getClient();
  let query = sb
    .from('events')
    .select('*')
    .eq('workspace_id', params.workspaceId)
    .is('archived_at', null)
    .or(`and(recurrence_rule.is.null,start_at.lte.${params.rangeEnd},end_at.gte.${params.rangeStart}),recurrence_rule.not.is.null`);

  if (params.channelId) {
    query = query.or(`owner_channel_id.eq.${params.channelId},shared_channel_ids.cs.{${params.channelId}}`);
  }

  const { data, error } = await query.order('start_at', { ascending: true });
  if (error) throw error;
  return (data ?? []).map(rowToEvent);
}

export async function fetchEvent(eventId: string): Promise<CalendarEvent | null> {
  const sb = getClient();
  const { data, error } = await sb
    .from('events')
    .select('*')
    .eq('id', eventId)
    .maybeSingle();

  if (error) throw error;
  return data ? rowToEvent(data) : null;
}

export async function createEvent(params: {
  workspace_id: string;
  owner_id: string;
  creator: EventAuthor;
  visibility: EventVisibility;
  owner_channel_id?: string | null;
  shared_channel_ids?: string[];
  title: string;
  description?: string | null;
  location?: string | null;
  start_at: string;
  end_at: string;
  is_all_day?: boolean;
  timezone: string;
  recurrence_rule?: string | null;
  status?: 'confirmed' | 'tentative' | 'cancelled';
  color?: string | null;
  reminders?: number[];
  metadata?: Record<string, any> | null;
}): Promise<CalendarEvent> {
  const sb = getClient();
  const insertData: TablesInsert<'events'> = {
    workspace_id: params.workspace_id,
    owner_id: params.owner_id,
    creator: params.creator as unknown as TablesInsert<'events'>['creator'],
    visibility: params.visibility,
    owner_channel_id: params.owner_channel_id ?? null,
    shared_channel_ids: params.visibility === 'public_channels' ? (params.shared_channel_ids ?? []) : [],
    title: params.title,
    description: params.description ?? null,
    location: params.location ?? null,
    start_at: params.start_at,
    end_at: params.end_at,
    is_all_day: params.is_all_day ?? false,
    timezone: params.timezone,
    recurrence_rule: params.recurrence_rule ?? null,
    status: params.status ?? 'confirmed',
    color: params.color ?? null,
    reminders: params.reminders ?? [],
    metadata: params.metadata ?? null,
  };

  const { data, error } = await sb
    .from('events')
    .insert(insertData)
    .select()
    .single();

  if (error) throw error;
  // (A) 생성 시점엔 event_reminders 가 아직 없어 reconcile 해도 무의미(scheduled:0).
  // per-user 리마인더 reconcile 은 직후 호출되는 setMyReminders 가 책임진다 — 불필요한 콜드스타트 callable 제거.
  return rowToEvent(data);
}

export async function updateEvent(
  eventId: string,
  updates: Partial<{
    title: string;
    description: string | null;
    location: string | null;
    start_at: string;
    end_at: string;
    is_all_day: boolean;
    timezone: string;
    recurrence_rule: string | null;
    visibility: EventVisibility;
    owner_channel_id: string | null;
    shared_channel_ids: string[];
    status: 'confirmed' | 'tentative' | 'cancelled';
    color: string | null;
    reminders: number[];
    metadata: Record<string, any> | null;
  }>,
): Promise<CalendarEvent> {
  const sb = getClient();
  const patch = { ...updates, updated_at: new Date().toISOString() };
  const { data, error } = await sb
    .from('events')
    .update(patch)
    .eq('id', eventId)
    .select()
    .single();

  if (error) throw error;
  // Phase 2 — 시각/리마인더 변경 시 Cloud Tasks 재schedule
  await callReconcileReminders(eventId);
  return rowToEvent(data);
}

export async function archiveEvent(eventId: string): Promise<void> {
  const sb = getClient();
  const { error } = await sb
    .from('events')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', eventId);
  if (error) throw error;
}

export async function deleteEvent(eventId: string): Promise<void> {
  const sb = getClient();
  const { error } = await sb.from('events').delete().eq('id', eventId);
  if (error) throw error;
  // Phase 2 — events 행 삭제됐으므로 server 가 자동으로 모든 task cancel
  await callReconcileReminders(eventId);
}

// ═══════════════════════════════
//  Attendees (초대)
// ═══════════════════════════════

export async function fetchAttendees(eventId: string): Promise<EventAttendee[]> {
  const sb = getClient();
  const { data, error } = await sb
    .from('event_attendees')
    .select('*')
    .eq('event_id', eventId);
  if (error) throw error;
  return (data ?? []).map(rowToAttendee);
}

export async function inviteAttendees(eventId: string, userIds: string[], organizerUserId?: string): Promise<void> {
  if (userIds.length === 0) return;
  const sb = getClient();
  const rows = userIds.map((uid) => ({
    event_id: eventId,
    user_id: uid,
    status: 'pending' as const,
    is_organizer: uid === organizerUserId,
  }));
  const { error } = await sb.from('event_attendees').upsert(rows, { onConflict: 'event_id,user_id' });
  if (error) throw error;
}

export async function respondToInvite(
  eventId: string,
  userId: string,
  status: AttendeeStatus,
): Promise<void> {
  const sb = getClient();
  const { error } = await sb
    .from('event_attendees')
    .update({ status, responded_at: new Date().toISOString() })
    .eq('event_id', eventId)
    .eq('user_id', userId);
  if (error) throw error;
}

export async function removeAttendee(eventId: string, userId: string): Promise<void> {
  const sb = getClient();
  const { error } = await sb
    .from('event_attendees')
    .delete()
    .eq('event_id', eventId)
    .eq('user_id', userId);
  if (error) throw error;
}

/**
 * 사용자가 attendee 인 event_id 목록 (declined 제외)
 * "내가 초대받은 일정" 필터용
 */
export async function fetchMyAttendingEventIds(userId: string): Promise<string[]> {
  const sb = getClient();
  const { data, error } = await sb
    .from('event_attendees')
    .select('event_id')
    .eq('user_id', userId)
    .neq('status', 'declined');
  if (error) throw error;
  return (data ?? []).map((r) => r.event_id);
}

// ═══════════════════════════════
//  Reminders (per-user 개인 리마인더)
// ═══════════════════════════════

/** 단일 일정에 대한 내 리마인더 (분 배열) 조회. 없으면 빈 배열 */
export async function fetchMyReminders(eventId: string, userId: string): Promise<number[]> {
  const sb = getClient();
  const { data, error } = await sb
    .from('event_reminders')
    .select('minutes')
    .eq('event_id', eventId)
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return Array.isArray(data?.minutes) ? data!.minutes.filter((n: any) => typeof n === 'number') : [];
}

/**
 * 내 리마인더 설정 (upsert). 빈 배열이면 행 삭제 (= 알림 없음).
 */
export async function setMyReminders(eventId: string, userId: string, minutes: number[]): Promise<void> {
  const sb = getClient();
  if (!minutes || minutes.length === 0) {
    const { error } = await sb
      .from('event_reminders')
      .delete()
      .eq('event_id', eventId)
      .eq('user_id', userId);
    if (error) throw error;
  } else {
    const { error } = await sb
      .from('event_reminders')
      .upsert(
        { event_id: eventId, user_id: userId, minutes, updated_at: new Date().toISOString() },
        { onConflict: 'event_id,user_id' },
      );
    if (error) throw error;
  }
  // Phase 2 — 리마인더 변경(추가/삭제)을 Cloud Tasks 에 반영. reconcile 은 멱등.
  // createEvent 시점의 reconcile 은 event_reminders 기록 전에 실행되므로(race), 여기서 재호출이 필수.
  await callReconcileReminders(eventId);
}

/**
 * 내 리마인더 일괄 조회 — event_id → minutes[] 맵.
 * CalendarProvider 가 range 의 일정들에 대해 useEventReminders 에 넘길 용도.
 * RLS 가 본인 행만 반환하므로 user_id 필터로 충분.
 */
export async function fetchMyRemindersMap(userId: string): Promise<Record<string, number[]>> {
  const sb = getClient();
  const { data, error } = await sb
    .from('event_reminders')
    .select('event_id, minutes')
    .eq('user_id', userId);
  if (error) throw error;
  const map: Record<string, number[]> = {};
  for (const row of data ?? []) {
    map[row.event_id] = Array.isArray(row.minutes) ? row.minutes.filter((n: any) => typeof n === 'number') : [];
  }
  return map;
}

// ═══════════════════════════════
//  Notifications (캘린더 전용 알림)
// ═══════════════════════════════

/** 본인의 unread + read 통합 알림 fetch (최근 N건) */
export async function fetchEventNotifications(params: {
  recipientUserId: string;
  limit?: number;
}): Promise<EventNotification[]> {
  const sb = getClient();
  const { data, error } = await sb
    .from('event_notifications')
    .select('*')
    .eq('recipient_user_id', params.recipientUserId)
    .order('created_at', { ascending: false })
    .limit(params.limit ?? 50);
  if (error) throw error;
  return (data ?? []).map(rowToNotification);
}

/** unread 카운트 + 가장 최근 invitation 의 created_at (토스트 트리거용) */
export async function fetchUnreadSummary(recipientUserId: string): Promise<{
  unreadCount: number;
  latestInvitationCreatedAt: string | null;
}> {
  const sb = getClient();
  const [unreadRes, latestInviteRes] = await Promise.all([
    sb.from('event_notifications')
      .select('id', { count: 'exact', head: true })
      .eq('recipient_user_id', recipientUserId)
      .eq('is_read', false),
    sb.from('event_notifications')
      .select('created_at')
      .eq('recipient_user_id', recipientUserId)
      .eq('type', 'invitation')
      .eq('is_read', false)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
  ]);

  if (unreadRes.error) throw unreadRes.error;
  if (latestInviteRes.error) throw latestInviteRes.error;

  return {
    unreadCount: unreadRes.count ?? 0,
    latestInvitationCreatedAt: latestInviteRes.data?.created_at ?? null,
  };
}

/** 알림 모달 진입 시 보이는 알림 자동 읽음 처리 (액션 필요한 초대도 unread 만 해제) */
export async function markNotificationsRead(notificationIds: string[]): Promise<void> {
  if (notificationIds.length === 0) return;
  const sb = getClient();
  const { error } = await sb
    .from('event_notifications')
    .update({ is_read: true })
    .in('id', notificationIds);
  if (error) throw error;
}

// ═══════════════════════════════
//  Recurrence Exceptions (Step 6)
// ═══════════════════════════════

export async function fetchExceptions(eventId: string): Promise<EventException[]> {
  const sb = getClient();
  const { data, error } = await sb
    .from('event_exceptions')
    .select('*')
    .eq('event_id', eventId);
  if (error) throw error;
  return (data ?? []).map(rowToException);
}

export async function cancelInstance(eventId: string, recurrenceId: string): Promise<void> {
  const sb = getClient();
  const { error } = await sb.from('event_exceptions').upsert(
    { event_id: eventId, recurrence_id: recurrenceId, is_cancelled: true, override: {} },
    { onConflict: 'event_id,recurrence_id' },
  );
  if (error) throw error;
}

export async function overrideInstance(
  eventId: string,
  recurrenceId: string,
  override: Record<string, any>,
): Promise<void> {
  const sb = getClient();
  const { error } = await sb.from('event_exceptions').upsert(
    { event_id: eventId, recurrence_id: recurrenceId, is_cancelled: false, override },
    { onConflict: 'event_id,recurrence_id' },
  );
  if (error) throw error;
}

// ═══════════════════════════════
//  Holidays (Step 12)
// ═══════════════════════════════

// 공휴일은 연도별 정적 데이터 — 모듈 메모리 캐시로 월 이동마다 재fetch 방지 (country|year 키).
// 같은 해 안의 월 이동은 동일 데이터라 네트워크 왕복 없이 즉시 반환된다.
const holidayCache = new Map<string, PlatformHoliday[]>();

export async function fetchHolidays(countryCode: string, year: number): Promise<PlatformHoliday[]> {
  const cacheKey = `${countryCode}|${year}`;
  const cached = holidayCache.get(cacheKey);
  if (cached) return cached;
  const sb = getClient();
  const startDate = `${year}-01-01`;
  const endDate = `${year}-12-31`;
  const { data, error } = await sb
    .from('platform_holidays')
    .select('*')
    .eq('country_code', countryCode)
    .gte('date', startDate)
    .lte('date', endDate)
    .order('date', { ascending: true });
  if (error) throw error;
  const result = (data ?? []).map(rowToHoliday);
  holidayCache.set(cacheKey, result);
  return result;
}

export async function fetchHolidayConfig(workspaceId: string): Promise<WorkspaceHolidayConfig | null> {
  const sb = getClient();
  const { data, error } = await sb
    .from('workspace_holiday_config')
    .select('*')
    .eq('workspace_id', workspaceId)
    .maybeSingle();
  if (error) throw error;
  return data ? rowToHolidayConfig(data) : null;
}

/**
 * 시드된 국가 목록 (platform_holidays 에서 distinct country_code)
 * 워크스페이스 admin 이 선택할 수 있는 국가 풀.
 */
let availableCountriesCache: string[] | null = null;

export async function fetchAvailableHolidayCountries(): Promise<string[]> {
  if (availableCountriesCache) return availableCountriesCache;
  const sb = getClient();
  const { data, error } = await sb
    .from('platform_holidays')
    .select('country_code')
    .order('country_code');
  if (error) throw error;
  const set = new Set<string>();
  for (const row of data ?? []) set.add(row.country_code);
  availableCountriesCache = Array.from(set);
  return availableCountriesCache;
}

// ═══════════════════════════════
//  Tasks UNION — 가상 일정 (채널별 / 워크스페이스 전체)
// ═══════════════════════════════

/** Post row → 가상 CalendarEvent (Task 마감일) 공통 변환기 */
function postToTaskEvent(p: any, channelId: string): CalendarEvent {
  const dueDate: string = p.metadata?.due_date;
  // All-day Task 마감일은 사용자의 로컬 timezone 의 자정 ~ 23:59 로 매핑.
  // UTC 기준으로 했더니 KST(UTC+9) 에서 1일 09:00 ~ 2일 08:59 로 해석돼
  // 캘린더 month/day view 의 isEventOnDate(start < d1 && end > d0) 가 두 날 모두 true → 중복 표시 버그.
  // 로컬 자정 ~ 로컬 23:59 면 어느 timezone 에서든 해당 날짜 셀 1개에만 걸림.
  const [y, m, d] = dueDate.split('-').map(Number);
  const startAt = new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
  const endAt = new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
  return {
    id: `task::${p.id}`,
    workspace_id: p.workspace_id,
    owner_id: p.author?.uid ?? '',
    visibility: 'public_channels' as EventVisibility,
    owner_channel_id: channelId,
    shared_channel_ids: [],
    title: p.title || '(제목 없음)',
    description: null,
    location: null,
    meeting_link: null,
    room_call_id: null,
    start_at: startAt,
    end_at: endAt,
    is_all_day: true,
    timezone: 'UTC',
    recurrence_rule: null,
    reminders: [],
    creator: (p.author as EventAuthor) ?? { uid: '', name: '' },
    attachments: [],
    status: 'confirmed' as const,
    color: null,
    cross_refs: [],
    source_post_id: p.id,
    source_board_id: p.board_id,
    is_auto_generated: true,
    scheduled_task_ids: null,
    comment_count: 0,
    metadata: {
      __task: true,
      post_id: p.id,
      board_id: p.board_id,
      channel_id: channelId,
      task_status: p.metadata?.status ?? 'requested',
      assignee: p.metadata?.assignee ?? null,
    },
    created_at: p.created_at ?? new Date().toISOString(),
    updated_at: null,
    archived_at: null,
  };
}

/**
 * 채널의 task 게시판(type='tasks') 에서 due_date 가 range 안에 있고
 * status != 'done' 인 게시글을 가상 CalendarEvent 로 변환.
 *
 * 합의 (H): Phase 1 읽기 전용. 가상 이벤트는 metadata.__task=true 로 분류.
 */
export async function fetchChannelTaskDeadlines(params: {
  workspaceId: string;
  channelId: string;
  rangeStart: string;   // ISO
  rangeEnd: string;     // ISO
}): Promise<CalendarEvent[]> {
  const sb = getClient();

  // 1) 채널의 tasks 게시판 ID 조회 (1개만)
  const { data: boards, error: boardErr } = await sb
    .from('boards')
    .select('id')
    .eq('workspace_id', params.workspaceId)
    .eq('channel_id', params.channelId)
    .eq('type', 'tasks')
    .is('archived_at', null)
    .limit(1);
  if (boardErr) throw boardErr;
  const boardId = boards?.[0]?.id as string | undefined;
  if (!boardId) return [];

  // 2) due_date 가 range 안에 있고 미완료 게시글
  const startDate = params.rangeStart.slice(0, 10);
  const endDate = params.rangeEnd.slice(0, 10);
  const { data: posts, error: postErr } = await sb
    .from('posts')
    .select('id, board_id, workspace_id, title, author, metadata, created_at')
    .eq('board_id', boardId)
    .neq('metadata->>status', 'done')
    .gte('metadata->>due_date', startDate)
    .lte('metadata->>due_date', endDate);
  if (postErr) throw postErr;

  return (posts ?? []).map((p: any) => postToTaskEvent(p, params.channelId));
}

/**
 * 메인 캘린더용 Tasks UNION — 사용자가 멤버인 모든 채널의 task 보드를 한 번에.
 *
 * @param channelIds 사용자가 속한 채널 id 배열 (caller 가 멤버십 필터링)
 */
export async function fetchUserAccessibleTaskDeadlines(params: {
  workspaceId: string;
  channelIds: string[];
  rangeStart: string;
  rangeEnd: string;
}): Promise<CalendarEvent[]> {
  if (params.channelIds.length === 0) return [];
  const sb = getClient();

  // 1) 사용자 채널들의 tasks 게시판 일괄 조회
  const { data: boards, error: boardErr } = await sb
    .from('boards')
    .select('id, channel_id')
    .eq('workspace_id', params.workspaceId)
    .in('channel_id', params.channelIds)
    .eq('type', 'tasks')
    .is('archived_at', null);
  if (boardErr) throw boardErr;
  if (!boards || boards.length === 0) return [];

  const boardIds = boards.map((b: any) => b.id);
  const boardToChannel: Record<string, string> = {};
  for (const b of boards) boardToChannel[b.id] = b.channel_id ?? '';

  // 2) 보드들의 미완료 task posts (due_date in range)
  const startDate = params.rangeStart.slice(0, 10);
  const endDate = params.rangeEnd.slice(0, 10);
  const { data: posts, error: postErr } = await sb
    .from('posts')
    .select('id, board_id, workspace_id, title, author, metadata, created_at')
    .in('board_id', boardIds)
    .neq('metadata->>status', 'done')
    .gte('metadata->>due_date', startDate)
    .lte('metadata->>due_date', endDate);
  if (postErr) throw postErr;

  return (posts ?? []).map((p: any) => postToTaskEvent(p, boardToChannel[p.board_id] ?? ''));
}

export async function upsertHolidayConfig(
  workspaceId: string,
  config: { country_codes: string[]; custom_holidays: any[] },
): Promise<WorkspaceHolidayConfig> {
  const sb = getClient();
  const { data, error } = await sb
    .from('workspace_holiday_config')
    .upsert({
      workspace_id: workspaceId,
      country_codes: config.country_codes,
      custom_holidays: config.custom_holidays,
      updated_at: new Date().toISOString(),
    })
    .select()
    .single();
  if (error) throw error;
  return rowToHolidayConfig(data);
}
